/**
 * Biodata Mandiri — Fase 3
 *
 * Halaman pegawai untuk mengisi dan memperbarui biodata pribadi. Data
 * langsung ter-update ke database utama tanpa validasi admin.
 * Terbagi menjadi 4 kategori (tab):
 *   1. Identitas & Domisili
 *   2. Pendidikan, Pajak & Rekening
 *   3. Keluarga (istri/suami + anak 1-5 dinamis)
 *   4. Fasilitas Kesehatan & Unggah Dokumen
 */
import { useEffect, useMemo, useState } from "react";
import {
  IdCard, GraduationCap, Users, HeartPulse, Save, Upload, Trash2,
  FileText, ChevronDown, ChevronUp, RefreshCw, CheckCircle2, ImageIcon,
} from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { AuthImage } from "@/components/AuthImage";
import { api, apiErrorMessage } from "@/lib/api";

// ------------------- Definisi field / kategori -------------------

const OPTIONS = {
  jenis_kelamin: ["LAKI - LAKI", "PEREMPUAN"],
  agama: ["ISLAM", "KRISTEN", "KATOLIK", "HINDU", "BUDDHA", "KONGHUCU", "LAINNYA"],
  status_kewarganegaraan: ["WNI", "WNA"],
  pendidikan: [
    "SD", "SMP", "SMA / SMK", "DIPLOMA 1", "DIPLOMA 2", "DIPLOMA 3",
    "STRATA 1", "STRATA 2", "STRATA 3",
  ],
  ptkp: ["TK/0", "TK/1", "TK/2", "TK/3", "K/0", "K/1", "K/2", "K/3"],
  status_pernikahan: ["BELUM MENIKAH", "SUDAH MENIKAH", "CERAI HIDUP", "CERAI MATI"],
};

const KATEGORI_1 = [
  { name: "nomor_induk_kependudukan_(nik)", label: "Nomor Induk Kependudukan (NIK)", type: "text", maxLength: 16 },
  { name: "tempat_lahir_pegawai", label: "Tempat Lahir", type: "text" },
  { name: "tanggal_lahir_pegawai", label: "Tanggal Lahir", type: "date" },
  { name: "alamat_sesuai_ktp", label: "Alamat Sesuai KTP", type: "textarea" },
  { name: "alamat_domisili", label: "Alamat Domisili", type: "textarea" },
  { name: "jenis_kelamin_pegawai", label: "Jenis Kelamin", type: "select", options: OPTIONS.jenis_kelamin },
  { name: "agama", label: "Agama", type: "select", options: OPTIONS.agama },
  { name: "status_kewarganegaraan", label: "Status Kewarganegaraan", type: "select", options: OPTIONS.status_kewarganegaraan },
  { name: "nomor_handphone", label: "Nomor Handphone", type: "text", placeholder: "+62..." },
  { name: "nomor_kartu_keluarga", label: "Nomor Kartu Keluarga", type: "text", maxLength: 16 },
];

const KATEGORI_2 = [
  { name: "tingkat_pendidikan_terakhir", label: "Tingkat Pendidikan Terakhir", type: "select", options: OPTIONS.pendidikan },
  { name: "jurusan_pendidikan", label: "Jurusan Pendidikan", type: "text" },
  { name: "status_pajak_ptkp", label: "Status Pajak (PTKP)", type: "select", options: OPTIONS.ptkp },
  { name: "npwp", label: "NPWP", type: "text", maxLength: 20 },
  { name: "nama_bank", label: "Nama Bank", type: "text" },
  { name: "no_rekening", label: "No. Rekening", type: "text" },
];

const KELUARGA_INTI = [
  { name: "status_pernikahan", label: "Status Pernikahan", type: "select", options: OPTIONS.status_pernikahan },
  { name: "nama_istri/suami", label: "Nama Istri / Suami", type: "text" },
  { name: "jenis_kelamin_istri/suami", label: "Jenis Kelamin Istri / Suami", type: "select", options: OPTIONS.jenis_kelamin },
  { name: "tempat_lahir_istri/suami", label: "Tempat Lahir Istri / Suami", type: "text" },
  { name: "tanggal_lahir_istri/suami", label: "Tanggal Lahir Istri / Suami", type: "date" },
];

function anakFields(i) {
  return [
    { name: `nama_anak_${i}`, label: `Nama Anak ${i}`, type: "text" },
    { name: `jenis_kelamin_anak_${i}`, label: `Jenis Kelamin Anak ${i}`, type: "select", options: OPTIONS.jenis_kelamin },
    { name: `tempat_lahir_anak_${i}`, label: `Tempat Lahir Anak ${i}`, type: "text" },
    { name: `tanggal_lahir_anak_${i}`, label: `Tanggal Lahir Anak ${i}`, type: "date" },
  ];
}

const KATEGORI_4 = [
  { name: "nama_fasilitas_kesehatan_(sesuai_bpjs_kesehatan)", label: "Nama Faskes (sesuai BPJS Kesehatan)", type: "text" },
  { name: "lokasi_fasilitas_kesehatan_(sesuai_bpjs_kesehatan)", label: "Lokasi Faskes (sesuai BPJS Kesehatan)", type: "text" },
  { name: "nama_fasilitas_kesehatan_(yang_sering_dikunjungi)", label: "Nama Faskes (yang sering dikunjungi)", type: "text" },
  { name: "lokasi_fasilitas_kesehatan_(yang_sering_dikunjungi)", label: "Lokasi Faskes (yang sering dikunjungi)", type: "text" },
];

const UPLOAD_FIELDS = [
  { name: "field_upload_foto_profil", label: "Foto Profil", accept: "image/jpeg,image/png,image/webp", isImage: true },
  { name: "field_upload_nik", label: "Scan/Foto KTP (NIK)", accept: "application/pdf,image/jpeg,image/png,image/webp", isImage: false },
  { name: "field_upload_kartu_keluarga", label: "Scan/Foto Kartu Keluarga", accept: "application/pdf,image/jpeg,image/png,image/webp", isImage: false },
  { name: "field_upload_ijazah", label: "Scan Ijazah Terakhir", accept: "application/pdf,image/jpeg,image/png,image/webp", isImage: false },
];

// ------------------- Reusable field renderers -------------------

function FieldRenderer({ def, value, onChange, testIdPrefix }) {
  const id = `${testIdPrefix}-${def.name}`;
  if (def.type === "select") {
    return (
      <div className="space-y-1.5">
        <Label className="text-xs font-bold uppercase tracking-wider text-slate-600">{def.label}</Label>
        <Select value={value || ""} onValueChange={(v) => onChange(def.name, v)}>
          <SelectTrigger data-testid={id}>
            <SelectValue placeholder="Pilih..." />
          </SelectTrigger>
          <SelectContent>
            {def.options.map((opt) => (
              <SelectItem key={opt} value={opt}>{opt}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    );
  }
  if (def.type === "textarea") {
    return (
      <div className="space-y-1.5 md:col-span-2">
        <Label className="text-xs font-bold uppercase tracking-wider text-slate-600">{def.label}</Label>
        <Textarea
          value={value || ""}
          onChange={(e) => onChange(def.name, e.target.value)}
          data-testid={id}
          rows={3}
        />
      </div>
    );
  }
  return (
    <div className="space-y-1.5">
      <Label className="text-xs font-bold uppercase tracking-wider text-slate-600">{def.label}</Label>
      <Input
        type={def.type || "text"}
        value={value || ""}
        placeholder={def.placeholder}
        maxLength={def.maxLength}
        onChange={(e) => onChange(def.name, e.target.value)}
        data-testid={id}
      />
    </div>
  );
}

function FieldGrid({ fields, values, onChange, prefix }) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      {fields.map((f) => (
        <FieldRenderer key={f.name} def={f} value={values[f.name]} onChange={onChange} testIdPrefix={prefix} />
      ))}
    </div>
  );
}

// ------------------- File upload card -------------------

function UploadCard({ def, biodata, onUploaded, onCleared }) {
  const [busy, setBusy] = useState(false);
  const path = biodata?.[def.name];
  const filename = biodata?.[`${def.name}_filename`];
  const contentType = biodata?.[`${def.name}_content_type`];

  async function handleUpload(file) {
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Ukuran file maksimal 5 MB");
      return;
    }
    setBusy(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const { data } = await api.post(`/biodata/upload/${def.name}`, form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`${def.label} berhasil diunggah`);
      onUploaded(data.biodata);
    } catch (e) {
      toast.error(apiErrorMessage(e));
    } finally {
      setBusy(false);
    }
  }

  async function handleClear() {
    if (!window.confirm(`Hapus dokumen ${def.label}?`)) return;
    setBusy(true);
    try {
      const { data } = await api.delete(`/biodata/upload/${def.name}`);
      toast.success("Dokumen dihapus");
      onCleared(data.biodata);
    } catch (e) {
      toast.error(apiErrorMessage(e));
    } finally {
      setBusy(false);
    }
  }

  const isImg = contentType?.startsWith("image/");

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4 flex flex-col gap-3" data-testid={`upload-card-${def.name}`}>
      <div className="flex items-center justify-between">
        <div>
          <div className="text-sm font-bold text-slate-900">{def.label}</div>
          <div className="text-[11px] text-slate-500">Maks 5 MB · PDF / JPG / PNG / WEBP</div>
        </div>
        {path ? (
          <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-bold text-emerald-700 bg-emerald-50 border border-emerald-100 rounded px-2 py-0.5">
            <CheckCircle2 size={11} /> Sudah Diunggah
          </span>
        ) : (
          <span className="text-[10px] uppercase tracking-widest font-bold text-slate-500 bg-slate-50 border border-slate-100 rounded px-2 py-0.5">
            Belum Diunggah
          </span>
        )}
      </div>

      {path ? (
        <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
          {isImg ? (
            <AuthImage
              path={path}
              kind="biodata"
              alt={def.label}
              className="w-full h-40 rounded-md"
              testId={`upload-preview-${def.name}`}
            />
          ) : (
            <div className="flex items-center gap-2 text-slate-700 text-sm">
              <FileText size={16} className="text-teal-700" />
              <span className="truncate" title={filename}>{filename || "Dokumen tersimpan"}</span>
            </div>
          )}
        </div>
      ) : null}

      <div className="flex flex-wrap gap-2">
        <label className="inline-flex">
          <input
            type="file"
            accept={def.accept}
            className="hidden"
            data-testid={`upload-input-${def.name}`}
            onChange={(e) => {
              const f = e.target.files?.[0];
              e.target.value = "";
              handleUpload(f);
            }}
          />
          <Button
            asChild
            size="sm"
            className="gap-1.5 bg-teal-700 hover:bg-teal-800 cursor-pointer"
            disabled={busy}
            data-testid={`upload-btn-${def.name}`}
          >
            <span><Upload size={13} /> {path ? "Ganti" : "Unggah"}</span>
          </Button>
        </label>
        {/* Tombol Hapus file dihilangkan (Keamanan Data — Feb 2026). Pegawai
           cukup menekan "Ganti" untuk menggantikan file yang sudah diunggah. */}
      </div>
    </div>
  );
}

// ------------------- Halaman utama -------------------

export default function BiodataMandiri() {
  const [biodata, setBiodata] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [tab, setTab] = useState("kat1");
  // Anak yang tampil (default: berdasarkan data terisi, minimal 1)
  const [anakVisible, setAnakVisible] = useState(1);

  async function load() {
    setLoading(true);
    try {
      const { data } = await api.get("/biodata/mine");
      setBiodata(data);
      // Auto-detect anak terisi
      const filled = [1, 2, 3, 4, 5].filter((i) =>
        String(data[`nama_anak_${i}`] || "").trim() !== ""
      );
      setAnakVisible(Math.max(1, filled.length));
    } catch (e) {
      toast.error(apiErrorMessage(e));
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => { load(); }, []);

  function handleChange(name, value) {
    setBiodata((prev) => ({ ...prev, [name]: value }));
  }

  async function handleSave() {
    if (!biodata) return;
    setSaving(true);
    try {
      // Bangun payload hanya berisi field text (bukan upload/meta).
      const allowed = new Set([
        ...KATEGORI_1.map((f) => f.name),
        ...KATEGORI_2.map((f) => f.name),
        ...KELUARGA_INTI.map((f) => f.name),
        ...KATEGORI_4.map((f) => f.name),
      ]);
      for (let i = 1; i <= 5; i++) {
        anakFields(i).forEach((f) => allowed.add(f.name));
      }
      const payload = {};
      allowed.forEach((k) => { payload[k] = biodata[k] || ""; });
      const { data } = await api.put("/biodata/mine", payload);
      setBiodata(data);
      toast.success("Biodata tersimpan");
    } catch (e) {
      toast.error(apiErrorMessage(e));
    } finally {
      setSaving(false);
    }
  }

  const kelengkapan = useMemo(() => {
    if (!biodata) return 0;
    const _f = (k) => String(biodata[k] || "").trim() !== "";
    let total = 0;
    let filled = 0;
    // 1) Base wajib (identitas + pendidikan/pajak/rekening + faskes)
    [...KATEGORI_1, ...KATEGORI_2, ...KATEGORI_4].forEach((f) => {
      total += 1;
      if (_f(f.name)) filled += 1;
    });
    // 2) Status pernikahan selalu wajib
    total += 1;
    if (_f("status_pernikahan")) filled += 1;
    // 3) Field pasangan (4 field) hanya bila SUDAH MENIKAH
    const punyaPasangan = String(biodata.status_pernikahan || "").trim().toUpperCase() === "SUDAH MENIKAH";
    if (punyaPasangan) {
      ["nama_istri/suami", "jenis_kelamin_istri/suami", "tempat_lahir_istri/suami", "tanggal_lahir_istri/suami"].forEach((k) => {
        total += 1;
        if (_f(k)) filled += 1;
      });
    }
    // 4) Anak — hanya anak yang namanya sudah di-declare
    for (let i = 1; i <= 5; i++) {
      if (!_f(`nama_anak_${i}`)) continue;
      total += 1; filled += 1; // nama_anak_i already filled
      [`jenis_kelamin_anak_${i}`, `tempat_lahir_anak_${i}`, `tanggal_lahir_anak_${i}`].forEach((k) => {
        total += 1;
        if (_f(k)) filled += 1;
      });
    }
    return total ? Math.round((filled / total) * 100) : 0;
  }, [biodata]);

  if (loading) {
    return <div className="p-8 text-slate-500">Memuat biodata…</div>;
  }
  if (!biodata) return null;

  return (
    <div className="p-4 sm:p-8 max-w-5xl mx-auto" data-testid="page-biodata-mandiri">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <div className="text-[11px] font-bold uppercase tracking-[0.24em] text-teal-700">Data Pribadi</div>
          <h1 className="font-display text-3xl sm:text-4xl font-extrabold text-slate-900 mt-1 leading-tight">
            Biodata Saya
          </h1>
          <p className="text-slate-500 text-sm mt-1">
            Perbarui data pribadi Anda. Setiap perubahan tersimpan langsung tanpa persetujuan.
          </p>
          <div className="mt-2 flex items-center gap-3">
            <div className="text-xs font-mono-clock text-slate-600">
              NPP: <span className="font-semibold text-slate-900">{biodata.npp}</span>
              &nbsp; · &nbsp; {biodata.nama_pegawai}
            </div>
          </div>
        </div>
        <div className="flex flex-col items-end gap-2">
          <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500">Kelengkapan</div>
          <div className="flex items-center gap-2 min-w-[180px]">
            <div className="flex-1 h-2 bg-slate-100 rounded-full overflow-hidden">
              <div
                className="h-full bg-teal-600 transition-all"
                style={{ width: `${kelengkapan}%` }}
                data-testid="biodata-progress-bar"
              />
            </div>
            <div className="text-sm font-bold text-slate-900 font-mono-clock" data-testid="biodata-progress-percent">
              {kelengkapan}%
            </div>
          </div>
          <Button variant="outline" size="sm" onClick={load} className="gap-2" data-testid="biodata-refresh">
            <RefreshCw size={13} /> Muat Ulang
          </Button>
        </div>
      </div>

      {/* Tabs */}
      <div className="mt-6">
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="flex-wrap h-auto" data-testid="biodata-tabs">
            <TabsTrigger value="kat1" data-testid="biodata-tab-kat1" className="gap-2">
              <IdCard size={13} /> Identitas & Domisili
            </TabsTrigger>
            <TabsTrigger value="kat2" data-testid="biodata-tab-kat2" className="gap-2">
              <GraduationCap size={13} /> Pendidikan, Pajak & Rekening
            </TabsTrigger>
            <TabsTrigger value="kat3" data-testid="biodata-tab-kat3" className="gap-2">
              <Users size={13} /> Keluarga
            </TabsTrigger>
            <TabsTrigger value="kat4" data-testid="biodata-tab-kat4" className="gap-2">
              <HeartPulse size={13} /> Kesehatan & Dokumen
            </TabsTrigger>
          </TabsList>

          {/* Kategori 1 */}
          <TabsContent value="kat1" className="mt-4">
            <div className="rounded-xl border border-slate-200 bg-white p-5">
              <FieldGrid fields={KATEGORI_1} values={biodata} onChange={handleChange} prefix="fld-1" />
            </div>
          </TabsContent>

          {/* Kategori 2 */}
          <TabsContent value="kat2" className="mt-4">
            <div className="rounded-xl border border-slate-200 bg-white p-5">
              <FieldGrid fields={KATEGORI_2} values={biodata} onChange={handleChange} prefix="fld-2" />
            </div>
          </TabsContent>

          {/* Kategori 3 — Keluarga (dinamis Anak 1..5) */}
          <TabsContent value="kat3" className="mt-4 space-y-4">
            <div className="rounded-xl border border-slate-200 bg-white p-5">
              <div className="text-[10px] font-bold uppercase tracking-widest text-teal-700 mb-3">
                Data Pasangan
              </div>
              <FieldGrid fields={KELUARGA_INTI} values={biodata} onChange={handleChange} prefix="fld-3-p" />
            </div>

            <div className="rounded-xl border border-slate-200 bg-white p-5">
              <div className="flex items-center justify-between mb-3">
                <div className="text-[10px] font-bold uppercase tracking-widest text-teal-700">
                  Data Anak ({anakVisible} dari 5)
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setAnakVisible((n) => Math.min(5, n + 1))}
                    disabled={anakVisible >= 5}
                    className="gap-1.5"
                    data-testid="anak-add-btn"
                  >
                    <ChevronDown size={13} /> Tambah Anak
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      if (anakVisible <= 1) return;
                      // Kosongkan field anak terakhir sebelum mengecilkan.
                      anakFields(anakVisible).forEach((f) => handleChange(f.name, ""));
                      setAnakVisible((n) => Math.max(1, n - 1));
                    }}
                    disabled={anakVisible <= 1}
                    className="gap-1.5"
                    data-testid="anak-remove-btn"
                  >
                    <ChevronUp size={13} /> Kurangi
                  </Button>
                </div>
              </div>
              <div className="space-y-5">
                {Array.from({ length: anakVisible }, (_, idx) => idx + 1).map((i) => (
                  <div key={i} className="rounded-lg border border-slate-100 bg-slate-50/50 p-4" data-testid={`anak-block-${i}`}>
                    <div className="text-xs font-bold text-slate-800 mb-3">Anak ke-{i}</div>
                    <FieldGrid
                      fields={anakFields(i)}
                      values={biodata}
                      onChange={handleChange}
                      prefix={`fld-3-a${i}`}
                    />
                  </div>
                ))}
              </div>
            </div>
          </TabsContent>

          {/* Kategori 4 — Kesehatan + Dokumen Upload */}
          <TabsContent value="kat4" className="mt-4 space-y-4">
            <div className="rounded-xl border border-slate-200 bg-white p-5">
              <div className="text-[10px] font-bold uppercase tracking-widest text-teal-700 mb-3">
                Fasilitas Kesehatan
              </div>
              <FieldGrid fields={KATEGORI_4} values={biodata} onChange={handleChange} prefix="fld-4" />
            </div>

            <div className="rounded-xl border border-slate-200 bg-white p-5">
              <div className="text-[10px] font-bold uppercase tracking-widest text-teal-700 mb-3 flex items-center gap-1.5">
                <ImageIcon size={13} /> Unggah Dokumen (Object Storage)
              </div>
              <p className="text-xs text-slate-500 mb-4">
                File tersimpan aman di object storage dan hanya bisa diakses oleh Anda dan Admin.
                Ganti file kapan pun dengan menekan tombol Unggah lagi.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {UPLOAD_FIELDS.map((def) => (
                  <UploadCard
                    key={def.name}
                    def={def}
                    biodata={biodata}
                    onUploaded={setBiodata}
                    onCleared={setBiodata}
                  />
                ))}
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </div>

      {/* Save bar (sticky) */}
      <div className="mt-6 sticky bottom-4 z-10">
        <div className="rounded-xl border border-teal-200 bg-white/95 backdrop-blur-sm shadow-lg p-4 flex flex-col sm:flex-row items-center justify-between gap-3">
          <div className="text-xs text-slate-600">
            Perubahan pada tab manapun tersimpan bersamaan saat Anda menekan
            <span className="font-semibold text-slate-900"> Simpan Biodata</span>.
            <span className="hidden sm:inline"> Field upload tersimpan otomatis saat berhasil diunggah.</span>
          </div>
          <Button
            onClick={handleSave}
            disabled={saving}
            className="gap-2 bg-teal-700 hover:bg-teal-800"
            data-testid="biodata-save-btn"
          >
            <Save size={14} /> {saving ? "Menyimpan…" : "Simpan Biodata"}
          </Button>
        </div>
      </div>
    </div>
  );
}
